import { ChangeDetectionStrategy, Component, Injector, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { RoleService } from '../services/role.service';
import { RoleModel } from '../role.model';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { UserService } from '@fi-app/user/user.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiRoleActionHelperService } from '../services/fi-role-action-helper.service';

@Component({
    selector: 'fi-role-list',
    templateUrl: './role-list.component.html',
    styleUrls: ['./role-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-role-list fi-relative' },
    standalone: false,
})
export class RoleListComponent extends FiRemoteStateFullDataSource<RoleModel> implements OnInit {
    public defaultSortColumnName = 'Name';
    public defaultSortDirection = false;
    public objectType = ObjectTypes.Role;
    public itemsListType = FiItemsListType.RolesList;

    private roleService = inject(RoleService);
    private statisticService = inject(StatisticService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private userService = inject(UserService);
    private fiRoleActionHelperService = inject(FiRoleActionHelperService);

    protected roleAccessRight = this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Role.Add);

    protected listItemActions: Readonly<IItemsListAction2[]> = this.fiRoleActionHelperService.getListItemActions();
    protected selectedItemsActions: Readonly<IItemsListAction2[]> =
        this.fiRoleActionHelperService.getSelectedItemsActions(this.confirmDeleteSelectedRoles.bind(this));

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.statisticService.Visit(ModuleType.Core);
    }

    private confirmDeleteSelectedRoles(): void {
        this.fiItemsDeleteHelperService
            .commonDeleteConfirm(ObjectTypes.Role, this.selectedItems())
            .then(() => this.deleteSelectedRoles())
            .catch(() => {});
    }

    private deleteSelectedRoles(): void {
        const selectedItems = this.selectedItems();
        const selectedItemIds = selectedItems.map((item) => item.Id);
        this.roleService.deleteRoles(selectedItemIds).then(() => {
            this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                ObjectTypes.Role,
                selectedItemIds,
                selectedItems[0].Name,
            );
            this.reloadData();
        });
    }

    protected goToRoleViewer(role: RoleModel): void {
        this.fiStateService.go(FiStateNames.Role.View, { Id: role.Id });
    }

    protected openUserViewDialog($event: MouseEvent, userId: number): void {
        $event.stopPropagation();
        this.userService.openUserViewDialog(userId);
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);

        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.roleService.getRolesListData(parameter);
    }
}
